import { useState, useEffect, useCallback, useMemo } from 'react';

// Ensure API_URL is always an empty string, never undefined
const API_URL = '';  // Use relative URLs

export const useMapData = (selectedDataset) => {
  const [mapSettings, setMapSettings] = useState({
    mapHeight: null,
  });
  const [mapData, setMapData] = useState({
    symbolPoints: [],
    adm1Data: {},
    mapOptions: {
      center: [0, 20],
      zoom: 2,  // Minimal default - will be overridden by extent fitting
      minZoom: 1,  // Reasonable lower bound for extent fitting
      maxZoom: 20,  // Reasonable upper bound
      projection: 'EPSG:3857'
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isViewDataLoaded, setIsViewDataLoaded] = useState(false); // Track if view-specific data has been loaded
  const [error, setError] = useState(null);

  // Define fetchMapSettings as a useCallback to prevent unnecessary re-creation
  const fetchMapSettings = useCallback(async () => {
    try {
      const response = await fetch(`/api/settings/map`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setMapSettings({
        mapHeight: data.map_height,
      });
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching map settings:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, []);

  // Fetch map settings
  useEffect(() => {
    fetchMapSettings();
  }, [fetchMapSettings]);

  // Define transformAdm1Data as a reusable function
  const transformAdm1Data = useCallback((adm1Data) => {
    return adm1Data.map(row => ({
      id: row.adm1_id,
      name: row.name,
      centroid: [row.centroid_lon, row.centroid_lat],
      zoom_extent: row.zoom_extent_min_lon ? [
        row.zoom_extent_min_lon,
        row.zoom_extent_min_lat,
        row.zoom_extent_max_lon,
        row.zoom_extent_max_lat
      ] : null
    }));
  }, []);

  // Define fetchAllData with useCallback to prevent unnecessary re-creation
  const fetchAllData = useCallback(async (viewCode) => {
    try {
      
      // Always fetch all data including symbol points
      const [symbolPointsResponse, adm1Response, mapOptionsResponse] = await Promise.all([
        fetch(`/api/views/${viewCode}/symbol-points`),
        fetch(`/api/views/${viewCode}/adm1`),
        fetch(`/api/views/${viewCode}/map-options`)
      ]);

      // Check if any request failed
      if (!symbolPointsResponse.ok || !adm1Response.ok || !mapOptionsResponse.ok) {
        throw new Error('One or more API requests failed');
      }

      // Parse all responses
      const [symbolPointsData, adm1Data, mapOptionsData] = await Promise.all([
        symbolPointsResponse.json(),
        adm1Response.json(),
        mapOptionsResponse.json()
      ]);

      // Transform ADM1 data
      const transformedAdm1Data = transformAdm1Data(adm1Data);

      // Update all data at once
      setMapData(prevData => {
        return {
          symbolPoints: symbolPointsData,
          adm1Data: { ...prevData.adm1Data, [viewCode]: transformedAdm1Data },
          mapOptions: mapOptionsData
        };
      });
      setIsViewDataLoaded(true); // Mark that view-specific data has been loaded
      setError(null);
    } catch (error) {
      // Error handling without verbose logging
      setMapData(prevData => ({
        symbolPoints: [],
        adm1Data: { ...prevData.adm1Data, [viewCode]: [] },
        mapOptions: null
      }));
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [transformAdm1Data]);

  // Fetch view-specific map data
  useEffect(() => {
    if (!selectedDataset) {
      setIsViewDataLoaded(false); // Reset when no dataset selected
      return;
    }
    
    const viewCode = selectedDataset.toUpperCase();
    setIsLoading(true);
    setIsViewDataLoaded(false); // Reset before fetching new view data
    fetchAllData(viewCode);
  }, [selectedDataset, fetchAllData]);

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => ({
    mapSettings,
    mapData,
    isLoading,
    isViewDataLoaded, // Expose this so App.jsx can wait for view-specific data
    error
  }), [mapSettings, mapData, isLoading, isViewDataLoaded, error]);
};
